import { createEntityId } from '@/core/EntityId';
import type { SurfaceLayerId } from '@/terrain/TerrainSurface';
import { cloneTagImage, fitTagImages, type TagImage } from './TagImage';

/**
 * Semantic tags — what the designer *means*, attached to the place or the thing they mean it about.
 *
 * ## The gap this fills
 *
 * The capture manifest is already good at facts: this is `Acer rubrum`, 5.4 m, 60 % through leaf
 * drop, right midground. What it has never been able to say is *intent*. A gravel rectangle beside a
 * bench is a gravel rectangle; whether it is a fire-pit lounge, a bin store, or the top of a drive is
 * knowledge that lives only in the designer's head, and an image model handed the geometry without it
 * will pick one at random — differently in every render, which is why two enhancements of the same
 * frame can come back as two different gardens.
 *
 * A tag is that missing sentence, written once and carried into every subsequent render.
 *
 * ## Why one document-level list rather than a field on each record
 *
 * A tag can be about a *place* — "this corner stays wild" — and a place owns no record. Splitting
 * location tags into their own array and object tags into fields on `FloraInstance` / `SiteObject`
 * would give one concept three storage sites, three serializers, three bits of UI, and three chances
 * to disagree. So the target is a discriminated union and the list is one array on the project. The
 * cost is that deleting an object has to sweep its tags, which {@link orphanTagIds} makes a one-liner.
 *
 * ## Deliberately free of Three.js, DOM, and the catalogs
 *
 * Everything here is plain data and pure functions over it, in the same spirit as `SceneManifest`:
 * the tag layer is unit-testable without a GPU, and the pieces that need a scene (picking, markers)
 * live in `editor/TagTool.ts`.
 */

/**
 * What a tag is attached to.
 *
 * `ground` targets a painted *material* rather than a patch of it — "the gravel on this site is
 * self-binding path gravel, not pea shingle" is a statement about every gravel texel, and tying it to
 * one is both harder to author and less useful. A specific patch is what a `location` tag is for.
 */
export type TagTarget =
  | { kind: 'location'; x: number; z: number; radiusMeters: number }
  | { kind: 'plant'; instanceId: string }
  | { kind: 'structure'; objectId: string }
  | { kind: 'ground'; layer: SurfaceLayerId }
  | { kind: 'site' };

export type TagTargetKind = TagTarget['kind'];

export const TAG_TARGET_KINDS: readonly TagTargetKind[] = [
  'location',
  'plant',
  'structure',
  'ground',
  'site',
];

/**
 * What kind of statement the tag makes.
 *
 * This is not decoration: the prompt builder words each category differently, because "dining
 * terrace" and "weathered cedar" ask an image model for completely different things — one is a
 * function it should furnish and light for, the other is a surface it should render. Lumping them
 * into undifferentiated free text throws that distinction away at exactly the point it matters.
 */
export type TagCategory = 'identity' | 'material' | 'use' | 'style' | 'condition' | 'avoid';

export const TAG_CATEGORIES: readonly TagCategory[] = [
  'identity',
  'material',
  'use',
  'style',
  'condition',
  'avoid',
];

export interface TagCategoryInfo {
  id: TagCategory;
  /** Title for the picker. */
  name: string;
  /** One line explaining what belongs here, shown as the picker's tooltip. */
  hint: string;
  /** Placeholder for the label field — an example of the register wanted. */
  example: string;
  /** Swatch colour, shared by the picker chip and the scene marker. */
  color: string;
}

export const TAG_CATEGORY_INFO: readonly TagCategoryInfo[] = [
  {
    id: 'identity',
    name: 'What it is',
    hint: 'Names the thing. Use when the geometry alone would be read as something else.',
    example: 'potting shed',
    color: '#7fb2ff',
  },
  {
    id: 'material',
    name: 'Material',
    hint: 'What it is made of or surfaced with — the detail a render should actually show.',
    example: 'weathered cedar board-and-batten',
    color: '#d4a24c',
  },
  {
    id: 'use',
    name: 'Use',
    hint: 'What the place is for. Drives furniture, wear, and how the light is staged.',
    example: 'evening dining terrace',
    color: '#69c08a',
  },
  {
    id: 'style',
    name: 'Style',
    hint: 'The aesthetic register — the vocabulary the whole treatment should sit inside.',
    example: 'Japanese dry garden',
    color: '#c48ad6',
  },
  {
    id: 'condition',
    name: 'Condition',
    hint: 'State and age. Freshly laid and twenty years settled do not look alike.',
    example: 'moss in the joints, twenty years settled',
    color: '#8fa3ad',
  },
  {
    id: 'avoid',
    name: 'Keep out',
    hint: 'What must not appear here. Becomes a negative instruction in the render prompt.',
    example: 'no furniture, no ornament',
    color: '#e0776b',
  },
];

export function tagCategoryInfo(category: TagCategory): TagCategoryInfo {
  return TAG_CATEGORY_INFO.find((info) => info.id === category) ?? (TAG_CATEGORY_INFO[0] as TagCategoryInfo);
}

/**
 * How hard the render prompt pushes a tag.
 *
 * Three levels, not a slider: the only decisions a designer actually makes here are "mention it",
 * "make sure it happens", and "this is the point of the picture", and a 0–1 weight would invite
 * fiddling with a number no downstream stage can honour precisely anyway.
 */
export type TagEmphasis = 'mention' | 'emphasise' | 'critical';

export const TAG_EMPHASES: readonly TagEmphasis[] = ['mention', 'emphasise', 'critical'];

/** Ordering weight, so the prompt can put the loudest tags where they will be read. */
export const EMPHASIS_WEIGHT: Record<TagEmphasis, number> = {
  mention: 1,
  emphasise: 2,
  critical: 3,
};

export interface SemanticTag {
  id: string;
  target: TagTarget;
  category: TagCategory;
  /** The noun phrase itself — short, and what the prompt quotes. */
  label: string;
  /** Optional elaboration, appended to the prompt line when present. */
  note?: string;
  emphasis: TagEmphasis;
  /** ISO 8601. Kept so the tag list can be shown newest-first without a separate ordering field. */
  createdAt: string;
  /**
   * Reference photographs, which travel with the tag into the render request.
   *
   * Words run out on anything whose identity is construction rather than dimension — a fence, a
   * gate, a pergola. See `TagImage.ts` for why the bytes live in the document and why every one of
   * them carries an intent.
   */
  images?: TagImage[];
}

/** Longest label accepted. A tag is a phrase; anything longer belongs in the note. */
export const MAX_TAG_LABEL_LENGTH = 64;
/** Longest note accepted — roughly two sentences, which is all a prompt line can carry. */
export const MAX_TAG_NOTE_LENGTH = 240;
/** Largest radius a location tag may claim, in metres. The site itself caps out at 200 m. */
export const MAX_TAG_RADIUS_METERS = 200;
/** Default reach of a freshly placed location tag — about a seating area. */
export const DEFAULT_TAG_RADIUS_METERS = 3;

export interface CreateSemanticTagOptions {
  target: TagTarget;
  label: string;
  category?: TagCategory;
  note?: string;
  emphasis?: TagEmphasis;
  /** Reference photographs. Held to the per-tag budget by {@link fitTagImages}. */
  images?: readonly TagImage[];
  /** Overrides `new Date()` so tests and snapshots stay stable. */
  now?: Date;
  /** Overrides the generated id, so an import or a test can be deterministic. */
  id?: string;
}

export function createSemanticTag(options: CreateSemanticTagOptions): SemanticTag {
  const tag: SemanticTag = {
    id: options.id ?? createEntityId('tag-'),
    target: normalizeTarget(options.target),
    category: TAG_CATEGORIES.includes(options.category as TagCategory)
      ? (options.category as TagCategory)
      : 'identity',
    label: normalizeLabel(options.label),
    emphasis: TAG_EMPHASES.includes(options.emphasis as TagEmphasis)
      ? (options.emphasis as TagEmphasis)
      : 'mention',
    createdAt: (options.now ?? new Date()).toISOString(),
  };
  const note = normalizeNote(options.note);
  if (note !== undefined) tag.note = note;
  const images = fitTagImages(options.images ?? []);
  if (images.length > 0) tag.images = images;
  return tag;
}

/** Collapse whitespace and clamp length. A label is a phrase, so newlines are not meaningful in it. */
export function normalizeLabel(value: string): string {
  return value.replace(/\s+/g, ' ').trim().slice(0, MAX_TAG_LABEL_LENGTH);
}

/** Same treatment for the note, but absent rather than empty — "no note" is not "an empty note". */
export function normalizeNote(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  const trimmed = value.replace(/\s+/g, ' ').trim().slice(0, MAX_TAG_NOTE_LENGTH);
  return trimmed.length > 0 ? trimmed : undefined;
}

/** Clamp a target's numbers into range, leaving reference targets untouched. */
export function normalizeTarget(target: TagTarget): TagTarget {
  if (target.kind !== 'location') return { ...target };
  return {
    kind: 'location',
    x: finite(target.x),
    z: finite(target.z),
    radiusMeters: clamp(finite(target.radiusMeters, DEFAULT_TAG_RADIUS_METERS), 0.25, MAX_TAG_RADIUS_METERS),
  };
}

/** Whether a tag would say anything at all. An empty label is a tag the user abandoned. */
export function isMeaningfulTag(tag: SemanticTag): boolean {
  return tag.label.length > 0;
}

/** The entity id a tag points at, or undefined for site and ground tags. */
export function taggedEntityId(tag: SemanticTag): string | undefined {
  if (tag.target.kind === 'plant') return tag.target.instanceId;
  if (tag.target.kind === 'structure') return tag.target.objectId;
  return undefined;
}

/**
 * Tags whose target no longer exists.
 *
 * Called after a delete so a removed bench does not leave "reclaimed teak, silvered" hanging in the
 * document, describing nothing, and quietly padding every future prompt.
 */
export function orphanTagIds(
  tags: readonly SemanticTag[],
  livePlantIds: ReadonlySet<string>,
  liveObjectIds: ReadonlySet<string>,
): string[] {
  return tags
    .filter((tag) => {
      if (tag.target.kind === 'plant') return !livePlantIds.has(tag.target.instanceId);
      if (tag.target.kind === 'structure') return !liveObjectIds.has(tag.target.objectId);
      return false;
    })
    .map((tag) => tag.id);
}

/** Deep copy, so a stored or undo-held record can never alias a live one. */
export function cloneTag(tag: SemanticTag): SemanticTag {
  const copy: SemanticTag = { ...tag, target: { ...tag.target } };
  if (tag.note === undefined) delete copy.note;
  if (tag.images === undefined) delete copy.images;
  else copy.images = tag.images.map(cloneTagImage);
  return copy;
}

/**
 * Whether this tag carries a picture that asks for something to be reproduced.
 *
 * `match` and `material` bind the render to an artifact; `mood` and `avoid` do not. Used to sort
 * which images get the scarce reference slots when a site has more of them than one request can
 * carry — a fence that must be built right outranks a palette suggestion.
 */
export function hasBindingImage(tag: SemanticTag): boolean {
  return (tag.images ?? []).some((image) => image.intent === 'match' || image.intent === 'material');
}

/**
 * The parts of a tag that decide how it sorts and how it reads in a prompt.
 *
 * Stated structurally rather than as `SemanticTag` so the reference tags placed on a photograph
 * before a site exists (`reference/ReferenceTag.ts`) sort and phrase identically. Those tags are not
 * document tags — they target image coordinates, not entities — but a designer who has learned that
 * "essential" outranks "mention" here should not have to learn it twice.
 */
export interface TagStatement {
  category: TagCategory;
  label: string;
  note?: string;
  emphasis: TagEmphasis;
}

/**
 * Sort for display and for the prompt: loudest first, then newest.
 *
 * Newest-second rather than alphabetical because a tag list is a working record — the thing you just
 * wrote is the thing you are still thinking about, and it should not sink into the middle of a list.
 */
export function compareTags(
  a: { emphasis: TagEmphasis; createdAt: string },
  b: { emphasis: TagEmphasis; createdAt: string },
): number {
  const weight = EMPHASIS_WEIGHT[b.emphasis] - EMPHASIS_WEIGHT[a.emphasis];
  if (weight !== 0) return weight;
  return b.createdAt.localeCompare(a.createdAt);
}

/**
 * One tag as a prompt fragment: the label, its note, and the emphasis made explicit.
 *
 * Emphasis is spelled out in words rather than left to the reader's inference from ordering, because
 * an image model does not weight a list by position the way a person skimming one does.
 */
export function tagPhrase(tag: TagStatement): string {
  const note = tag.note !== undefined ? ` — ${tag.note}` : '';
  if (tag.category === 'avoid') return `must NOT include ${tag.label}${note}`;
  switch (tag.emphasis) {
    case 'critical':
      return `${tag.label}${note} (this is essential — the render is wrong without it)`;
    case 'emphasise':
      return `${tag.label}${note} (make this clearly readable)`;
    default:
      return `${tag.label}${note}`;
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function finite(value: number, fallback = 0): number {
  return Number.isFinite(value) ? value : fallback;
}
